import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { X, Smartphone, Download, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import PublicHeader from '@/components/layout/PublicHeader';

// All 16 screenshots in order with friendly labels
const SCREENSHOTS = [
  { src: '/app-screenshots/1-screenshot-app-login.png',        label: 'Login' },
  { src: '/app-screenshots/2-screenshot-knowledgelab.png',     label: 'Knowledge Lab' },
  { src: '/app-screenshots/3-screenshot-home-1.png',           label: 'Home' },
  { src: '/app-screenshots/4-screenshot-home-2.png',           label: 'Dashboard' },
  { src: '/app-screenshots/5-screenshot-home-3.png',           label: 'Overview' },
  { src: '/app-screenshots/6-screenshot-mood-1.png',           label: 'Mood Tracking' },
  { src: '/app-screenshots/7-screenshot-mood-2.png',           label: 'Mood Insights' },
  { src: '/app-screenshots/8-screenshot-mood-3.png',           label: 'Mood History' },
  { src: '/app-screenshots/9-screenshot-mood-4.png',           label: 'Mood Analytics' },
  { src: '/app-screenshots/10-screenshot-tasks.png',           label: 'Tasks' },
  { src: '/app-screenshots/11-screenshot-chat-1.png',          label: 'Chat' },
  { src: '/app-screenshots/12-screenshot-chat-2.png',          label: 'Messaging' },
  { src: '/app-screenshots/13-screenshot-people-experts.png',  label: 'Experts' },
  { src: '/app-screenshots/14-screenshot-people-training.png', label: 'Training' },
  { src: '/app-screenshots/15-screenshot-profile-1.png',       label: 'Profile' },
  { src: '/app-screenshots/16-screenshot-profile-2.png',       label: 'Settings' },
];

// Google Play Store URL — update this when the app is published
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=app.kstreak';

// Phone frame wrapper
const PhoneFrame = ({ src, label, size = 'md', dimmed = false }) => {
  const sizes = {
    sm: { outer: 'w-36', inner: 'rounded-[18px]', notch: 'w-12 h-4' },
    md: { outer: 'w-48', inner: 'rounded-[24px]', notch: 'w-16 h-5' },
    lg: { outer: 'w-64', inner: 'rounded-[32px]', notch: 'w-20 h-6' },
  };
  const s = sizes[size];

  return (
    <div
      className={`${s.outer} flex-shrink-0 transition-all duration-300 ${
        dimmed ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
      }`}
    >
      {/* Phone shell */}
      <div
        className={`relative bg-[#0A0A0A] ${s.inner} shadow-2xl overflow-hidden border-4 border-[#1a1a1a]`}
        style={{ aspectRatio: '9/19' }}
      >
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
          <div className={`${s.notch} bg-[#0A0A0A] rounded-b-xl`} />
        </div>
        {/* Screenshot */}
        <img
          src={src}
          alt={label}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      {/* Label */}
      <p className="text-center text-xs text-[#6B7280] mt-2 font-medium">{label}</p>
    </div>
  );
};

// Google Play download modal
const DownloadModal = ({ onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
      {/* Header */}
      <div className="bg-[#002FA7] px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">KStreak for Android</p>
            <p className="text-white/60 text-xs">Google Play Store</p>
          </div>
        </div>
        <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="px-6 py-5 space-y-4">
        <div className="flex items-center gap-3 p-3 bg-[#F3F4F6] rounded-xl">
          <img src="/logo.png" alt="KStreak" className="w-12 h-12 rounded-xl object-contain" />
          <div>
            <p className="font-semibold text-[#0A0A0A] text-sm">KStreak</p>
            <p className="text-xs text-[#4B5563]">Knowledge Management App</p>
            <div className="flex items-center gap-1 mt-1">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-3 h-3 fill-[#FF9500] text-[#FF9500]" />
              ))}
              <span className="text-xs text-[#6B7280] ml-1">5.0</span>
            </div>
          </div>
        </div>

        <p className="text-sm text-[#4B5563] leading-relaxed">
          You're about to be redirected to the <strong>Google Play Store</strong> to download KStreak for Android. The app is free to download.
        </p>

        <div className="space-y-2 text-xs text-[#6B7280]">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[#34C759]" />
            Free download
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[#34C759]" />
            Requires Android 8.0 or higher
          </div>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-[#34C759]" />
            Compatible with phones and tablets
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 pb-6 flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="flex-1 bg-[#002FA7] hover:bg-[#00227A] gap-2"
          onClick={() => { window.open(PLAY_STORE_URL, '_blank'); onClose(); }}
        >
          <Download className="w-4 h-4" />
          Download Now
        </Button>
      </div>
    </div>
  </div>
);

const MobileAppPage = () => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef(null);
  const total = SCREENSHOTS.length;

  // Auto-advance carousel every 3 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;
    autoPlayRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % total);
    }, 3000);
    return () => clearInterval(autoPlayRef.current);
  }, [isAutoPlaying, total]);

  const goTo = (idx) => {
    setActiveIndex((idx + total) % total);
    setIsAutoPlaying(false);
    // Resume auto-play after 6 seconds of inactivity
    clearInterval(autoPlayRef.current);
    autoPlayRef.current = setTimeout(() => setIsAutoPlaying(true), 6000);
  };

  const prev = () => goTo(activeIndex - 1);
  const next = () => goTo(activeIndex + 1);

  // Get 5 visible screenshots: prev-prev, prev, active, next, next-next
  const getVisible = () => {
    return [-2, -1, 0, 1, 2].map(offset => ({
      ...SCREENSHOTS[(activeIndex + offset + total) % total],
      offset,
    }));
  };

  return (
    <div className="min-h-screen bg-white">

      <PublicHeader />

      {/* ── Hero Section ───────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#002FA7]/5 via-white to-[#FF9500]/5 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#002FA7]/10 text-[#002FA7] text-xs font-medium rounded-full mb-5">
            <Smartphone className="w-3 h-3" />
            Now Available on Android
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A0A0A] tracking-tight leading-tight">
            KStreak in Your <span className="text-[#002FA7]">Pocket</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#4B5563] max-w-2xl mx-auto leading-relaxed">
            Capture knowledge, track tasks, chat with your team, and grow your expertise —
            anywhere, anytime. The full power of KStreak now on your mobile device.
          </p>

          {/* Store Badges */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">

            {/* Google Play — ACTIVE */}
            <button
              onClick={() => setShowModal(true)}
              className="group flex items-center gap-3 bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white px-5 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
            >
              <svg viewBox="0 0 24 24" className="w-7 h-7 flex-shrink-0" fill="none">
                <path d="M3.18 1.79 13.64 12 3.18 22.21A2 2 0 0 1 2 20.5v-17a2 2 0 0 1 1.18-1.71Z" fill="#EA4335"/>
                <path d="m13.64 12 3.55 3.55-9.91 5.73a2 2 0 0 1-2.02-.07L13.64 12Z" fill="#FBBC04"/>
                <path d="m20.47 10.18-3.28-1.9-3.55 3.72 3.55 3.55 3.3-1.91a2 2 0 0 0 0-3.46Z" fill="#4285F4"/>
                <path d="M5.26 2.79a2 2 0 0 1 2.02-.07l9.91 5.73L13.64 12 5.26 2.79Z" fill="#34A853"/>
              </svg>
              <div className="text-left">
                <p className="text-[10px] text-white/60 leading-none">GET IT ON</p>
                <p className="text-base font-semibold leading-tight">Google Play</p>
              </div>
            </button>

            {/* Apple App Store — COMING SOON */}
            <div className="relative group cursor-not-allowed">
              <div className="flex items-center gap-3 bg-[#0A0A0A]/30 text-white/50 px-5 py-3 rounded-xl border-2 border-dashed border-[#D1D5DB] select-none">
                <svg viewBox="0 0 24 24" className="w-7 h-7 flex-shrink-0 opacity-50" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <p className="text-[10px] leading-none opacity-60">DOWNLOAD ON THE</p>
                  <p className="text-base font-semibold leading-tight opacity-60">App Store</p>
                </div>
              </div>
              {/* Coming soon badge */}
              <span className="absolute -top-2.5 -right-2.5 bg-[#FF9500] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide shadow">
                Coming Soon
              </span>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                <div className="bg-[#0A0A0A] text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                  iOS app coming soon — stay tuned!
                </div>
              </div>
            </div>

            {/* Amazon Appstore — COMING SOON */}
            <div className="relative group cursor-not-allowed">
              <div className="flex items-center gap-3 bg-[#0A0A0A]/30 text-white/50 px-5 py-3 rounded-xl border-2 border-dashed border-[#D1D5DB] select-none">
                <svg viewBox="0 0 24 24" className="w-7 h-7 flex-shrink-0 opacity-50" fill="currentColor">
                  <path d="M13.958 10.09c0 1.232.029 2.256-.591 3.351-.502.891-1.301 1.438-2.186 1.438-1.214 0-1.922-.924-1.922-2.292 0-2.692 2.415-3.182 4.699-3.182v.685zm3.186 7.705a.66.66 0 0 1-.748.075c-1.052-.873-1.24-1.279-1.815-2.112-1.734 1.768-2.962 2.296-5.21 2.296-2.66 0-4.733-1.641-4.733-4.925 0-2.565 1.391-4.309 3.37-5.164 1.715-.754 4.11-.891 5.942-1.099v-.41c0-.753.06-1.642-.384-2.294-.385-.581-1.124-.82-1.775-.82-1.205 0-2.277.618-2.54 1.897-.054.285-.261.567-.547.582l-3.065-.333c-.259-.056-.548-.266-.472-.66C5.752 2.235 8.343 1.5 10.67 1.5c1.19 0 2.747.317 3.686 1.218 1.19 1.111 1.076 2.594 1.076 4.208v3.814c0 1.145.476 1.648.923 2.267.157.221.191.487-.008.653l-1.203 1.135zm3.542 1.937c-.502.331-1.178.555-1.717.211-.964-.637-1.14-1.868-1.14-2.755l.018-5.753c0-1.618.108-3.108-.855-4.241C16.009.73 13.895.25 12.008.25 8.49.25 5.578 1.488 4.841 5.188c-.104.533.277.815.617.895l2.997.321c.312-.012.54-.319.588-.622.281-1.368 1.429-2.029 2.712-2.029.695 0 1.481.256 1.892.878.47.694.414 1.638.414 2.426v.434c-1.95.218-4.502.36-6.33 1.15-2.1.892-3.571 2.716-3.571 5.39 0 3.431 2.16 5.143 4.949 5.143 2.352 0 3.637-.551 5.453-2.394.6.868.798 1.288 1.895 2.199.248.133.553.124.771-.071l.003.002 2.408-2.113c.211-.175.181-.461-.011-.65z"/>
                </svg>
                <div className="text-left">
                  <p className="text-[10px] leading-none opacity-60">AVAILABLE AT</p>
                  <p className="text-base font-semibold leading-tight opacity-60">Amazon Appstore</p>
                </div>
              </div>
              {/* Coming soon badge */}
              <span className="absolute -top-2.5 -right-2.5 bg-[#FF9500] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide shadow">
                Coming Soon
              </span>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                <div className="bg-[#0A0A0A] text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                  Amazon Appstore coming soon!
                </div>
              </div>
            </div>

          </div>

          {/* Rating row */}
          <div className="mt-6 flex items-center justify-center gap-6 text-sm text-[#6B7280]">
            <div className="flex items-center gap-1.5">
              <div className="flex">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-[#FF9500] text-[#FF9500]" />)}
              </div>
              <span className="font-medium text-[#0A0A0A]">5.0</span>
              <span>on Google Play</span>
            </div>
            <span className="text-[#E5E7EB]">|</span>
            <span>Free to download</span>
            <span className="text-[#E5E7EB]">|</span>
            <span>Android 8.0+</span>
          </div>
        </div>
      </section>

      {/* ── Screenshot Carousel ────────────────────────────────────────────── */}
      <section className="py-16 bg-[#F9FAFB] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">See It in Action</h2>
            <p className="mt-2 text-[#4B5563]">Swipe through the app and explore every feature</p>
          </div>

          {/* Carousel */}
          <div className="relative flex items-center justify-center gap-4 md:gap-6 h-[520px] md:h-[600px]">
            {/* Prev button */}
            <button
              onClick={prev}
              className="absolute left-0 z-20 w-10 h-10 rounded-full bg-white border border-[#E5E7EB] shadow-md flex items-center justify-center hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Phones */}
            <div className="flex items-center justify-center gap-3 md:gap-5">
              {getVisible().map(({ src, label, offset }) => (
                <div
                  key={`${src}-${offset}`}
                  onClick={() => offset !== 0 && goTo(activeIndex + offset)}
                  className={offset !== 0 ? 'cursor-pointer' : ''}
                >
                  <PhoneFrame
                    src={src}
                    label={label}
                    size={offset === 0 ? 'lg' : Math.abs(offset) === 1 ? 'md' : 'sm'}
                    dimmed={Math.abs(offset) === 2}
                  />
                </div>
              ))}
            </div>

            {/* Next button */}
            <button
              onClick={next}
              className="absolute right-0 z-20 w-10 h-10 rounded-full bg-white border border-[#E5E7EB] shadow-md flex items-center justify-center hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-all"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Dot indicators */}
          <div className="flex items-center justify-center gap-1.5 mt-6 flex-wrap">
            {SCREENSHOTS.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`rounded-full transition-all duration-300 ${
                  i === activeIndex
                    ? 'w-6 h-2 bg-[#002FA7]'
                    : 'w-2 h-2 bg-[#D1D5DB] hover:bg-[#9CA3AF]'
                }`}
              />
            ))}
          </div>

          {/* Screenshot counter */}
          <p className="text-center text-xs text-[#9CA3AF] mt-3">
            {activeIndex + 1} / {total} — {SCREENSHOTS[activeIndex].label}
          </p>
        </div>
      </section>

      {/* ── All Screenshots Grid ───────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">Every Screen, Every Feature</h2>
            <p className="mt-2 text-[#4B5563]">A complete look at the KStreak mobile experience</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-6 justify-items-center">
            {SCREENSHOTS.map((s, i) => (
              <div
                key={i}
                onClick={() => {
                  setActiveIndex(i);
                  setIsAutoPlaying(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="cursor-pointer group"
              >
                <PhoneFrame src={s.src} label={s.label} size="md" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features Strip ─────────────────────────────────────────────────── */}
      <section className="py-14 bg-[#002FA7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { value: '16+', label: 'App Screens' },
              { value: 'Free', label: 'To Download' },
              { value: '5.0★', label: 'Rating' },
              { value: 'Android', label: 'Available Now' },
            ].map(({ value, label }) => (
              <div key={label}>
                <p className="text-3xl font-bold">{value}</p>
                <p className="text-white/70 text-sm mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ────────────────────────────────────────────────────── */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">Ready to Get Started?</h2>
          <p className="mt-3 text-[#4B5563]">
            Download the KStreak app on Android today, or sign up on the web and sync your knowledge across all devices.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] text-white px-6 py-3 rounded-xl font-medium transition-all hover:scale-105 shadow-lg"
            >
              <Download className="w-4 h-4" />
              Download on Google Play
            </button>
            <Link to="/register">
              <button className="flex items-center gap-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 px-6 py-3 rounded-xl font-medium transition-all">
                Start Free on Web
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer (same as LandingPage) ───────────────────────────────────── */}
      <footer className="py-8 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo.png" alt="KStreak" className="w-6 h-6 rounded object-contain" />
                <span className="font-semibold text-white text-sm">KStreak</span>
              </div>
              <p className="text-xs text-[#9CA3AF]">AI-Enhanced Knowledge Management for individuals and organizations.</p>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-white mb-2">Product</h4>
              <div className="space-y-1">
                {['Features', 'Pricing', 'Free Trial', 'Enterprise'].map(l => (
                  <a key={l} href={`/#${l.toLowerCase().replace(' ', '-')}`} className="block text-xs text-[#9CA3AF] hover:text-white transition-colors">{l}</a>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-white mb-2">Resources</h4>
              <div className="space-y-1">
                {['Documentation', 'API Reference', 'Blog', 'Webinars'].map(l => (
                  <span key={l} className="block text-xs text-[#9CA3AF]">{l}</span>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-white mb-2">Mobile App</h4>
              <div className="space-y-1">
                <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="block text-xs text-[#9CA3AF] hover:text-white transition-colors">Google Play Store</a>
                <span className="block text-xs text-[#9CA3AF]">App Store <span className="text-[#FF9500]">(Soon)</span></span>
                <span className="block text-xs text-[#9CA3AF]">Amazon Appstore <span className="text-[#FF9500]">(Soon)</span></span>
              </div>
            </div>
          </div>
          <div className="border-t border-[#1F1F1F] pt-4 flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs text-[#9CA3AF]">&copy; 2026 KStreak. All rights reserved.</p>
            <div className="flex gap-4 mt-2 md:mt-0">
              {['Privacy Policy', 'Terms of Service', 'Security'].map(l => (
                <span key={l} className="text-xs text-[#9CA3AF] hover:text-white cursor-pointer transition-colors">{l}</span>
              ))}
            </div>
          </div>
        </div>
      </footer>

      {/* Download Modal */}
      {showModal && <DownloadModal onClose={() => setShowModal(false)} />}
    </div>
  );
};

export default MobileAppPage;
